"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 47: NES-QJ, the two-game Nintendo multicart
 * ------------------------------------------------------------------ *
 * "Super Spike V'Ball + Nintendo World Cup" is two MMC3 games in one
 * pak, cut apart by a single block-select bit that the MMC3 only ever
 * sees as its PRG-RAM window:
 *
 *   $6000-$7FFF   xxxx xxxB
 *                         └── PRG ROM A17 and CHR ROM A17
 *
 * Each block is 128 KB of PRG and 128 KB of CHR, so the bit is the whole
 * outer addressing:
 *
 *   value  PRG window              CHR window
 *   0      $00000-$1FFFF (128 KB)  $00000-$1FFFF
 *   1      $20000-$3FFFF (128 KB)  $20000-$3FFFF
 *
 * It is mapper 37 with a one-bit register and no address lines forced:
 * only A17 is replaced, and the MMC3's own A16 reaches the ROM untouched,
 * which is why a block holds sixteen 8 KB banks rather than eight. The
 * same translation trick applies -- apply the block to every bank number
 * the MMC3 hands the cartridge, in the two bank-loading helpers -- and
 * the hardwired $C000/$E000 windows come out right for the same reason.
 *
 * The register is write-only, and since the MMC3 believes it is driving
 * its RAM, the $A001 write protect bit gates the write; the board has no
 * PRG RAM for the window to be shared with.
 *
 * Used by Super Spike V'Ball + Nintendo World Cup only.
 * See https://www.nesdev.org/wiki/INES_Mapper_047
 */

function register(Mappers) {
  Mappers[47] = function (nes) {
    // The MMC3 constructor sets up command, bankSelect and the IRQ state.
    Mappers[4].call(this, nes);

    // Block select: the latched bit, 0 or 1.
    this.blockSelect = 0;
  };

  // Inherit from MMC3 (which inherits from NROM).
  Mappers[47].prototype = new Mappers[4]();

  Mappers[47].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      // The MMC3 sees this window as its PRG-RAM, so its write protect
      // ($A001 bit 1) gates the latch exactly as it would gate a RAM write.
      // Nothing is stored: there is no RAM here, and the latch is not
      // readable.
      if (this.prgRamWritable) {
        this.setBlockSelect(value & 0x01);
      }
      return;
    }

    Mappers[4].prototype.write.apply(this, arguments);
  };

  Mappers[47].prototype.setBlockSelect = function (value) {
    if (value === this.blockSelect) {
      return;
    }

    this.blockSelect = value;

    // Every window in both address spaces moved at once.
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  // An 8 KB PRG bank number from the MMC3, translated into the ROM.
  Mappers[47].prototype.translatePrgBank = function (bank8k) {
    // A17 is the block bit, bit 4 of an 8 KB bank number. A16 and below are
    // the MMC3's, so a block is the full 128 KB: all sixteen 8 KB banks.
    return (this.blockSelect << 4) + (bank8k & 0x0f);
  };

  // A 1 KB CHR bank number from the MMC3, translated into the ROM.
  Mappers[47].prototype.translateChrBank = function (bank1k) {
    // CHR A17 is the block bit as well, so the block is 128 banks of 1 KB
    // and the MMC3's own A17 never reaches the ROM.
    return (this.blockSelect << 7) + (bank1k & 0x7f);
  };

  // Every cartridge window is loaded through these two helpers, so the
  // translation goes here rather than in a copy of updatePrgBanks() /
  // updateChrBanks(). The MMC3's loadCHRROM() uses loadVromBank() and is
  // deliberately not touched: it fills the pattern tables with the head of
  // the CHR ROM before any window is derived.
  Mappers[47].prototype.load8kRomBank = function (bank8k, address) {
    Mappers[4].prototype.load8kRomBank.call(
      this,
      this.translatePrgBank(bank8k),
      address,
    );
  };

  Mappers[47].prototype.load1kVromBank = function (bank1k, address) {
    Mappers[4].prototype.load1kVromBank.call(
      this,
      this.translateChrBank(bank1k),
      address,
    );
  };

  Mappers[47].prototype.reset = function () {
    Mappers[4].prototype.reset.apply(this);

    // The outer latch is cleared by the cartridge's reset line, so a soft
    // reset goes back to the menu. The windows have to be re-derived here:
    // clearing the latch without re-mapping would leave the ROM windows
    // pointing at the banks the old value selected.
    this.blockSelect = 0;
    this.updatePrgBanks();
    this.updateChrBanks();
  };

  Mappers[47].prototype.toJSON = function () {
    var s = Mappers[4].prototype.toJSON.apply(this);
    s.blockSelect = this.blockSelect;
    return s;
  };

  Mappers[47].prototype.fromJSON = function (s) {
    // The latch has to be back before the MMC3's restore re-derives the
    // windows from it, or they would be mapped for the old value.
    this.blockSelect = (s.blockSelect || 0) & 0x01;
    Mappers[4].prototype.fromJSON.apply(this, arguments);
  };
}

module.exports = { register: register };
